import React from 'react';

import { usePagination, useSearchParam as defaultUseSearchParam, useTableSelection } from '@hooks';
import { regexValidator } from '@utils/validator';
import { SORT_OPTIONS } from '@api/constants';

const defaultValidateSortOptions = (value) => SORT_OPTIONS.some(({ key }) => key === value);

export const useRecordTable = ({
  useList,
  listQueryVariables,
  getDataSource,
  validateQueryParams = regexValidator,
  validateFilterParams,
  validateSecondFilterParams,
  validateSortParams = defaultValidateSortOptions,
  transformFilter,
  transformSecondFilter,
  rowKey,
  perPage = 6,
  mutations = {},
  useSearchParam = defaultUseSearchParam,
}) => {
  const pagination = usePagination({ initialPerPage: perPage, useSearchParam });
  const [query, setQuery] = useSearchParam({ key: 'query', validate: validateQueryParams });
  const [filter, setFilter] = useSearchParam({ key: 'filter', validate: validateFilterParams });
  const [secondFilter, setSecondFilter] = useSearchParam({ key: 'secondFilter', validate: validateSecondFilterParams });
  const [sort, setSort] = useSearchParam({ key: 'sort', validate: validateSortParams });

  const buildQuery = {
    page: pagination.page,
    perPage: pagination.perPage,
    sort,
    query,
    ...transformFilter(filter),
    ...listQueryVariables,
  };

  const listQuery = useList(
    transformSecondFilter
      ? {
          ...buildQuery,
          ...transformSecondFilter(secondFilter),
        }
      : {
          ...buildQuery,
        },
    { onSuccess: (response) => pagination.setTotal(response.total) },
  );

  const dataSource = getDataSource(listQuery);

  const selection = useTableSelection({ pageData: dataSource, rowKey });
  const extra = Object.keys(mutations).reduce(
    (acc, name) => {
      const [m, q] = mutations[name].call(null, { onSuccess: selection.clearSelectedKeys });
      acc.mutations[name] = m;
      acc.queries[name] = q;
      return acc;
    },
    { mutations: {}, queries: {} },
  );

  const withPageReset = React.useCallback(
    (fn) => (...args) => {
      pagination.reset();
      return fn(...args);
    },
    [pagination],
  );

  return {
    dataSource,
    pagination,
    query: { value: query, set: withPageReset(setQuery) },
    filter: { value: filter, set: withPageReset(setFilter) },
    secondFilter: { value: secondFilter, set: withPageReset(setSecondFilter) },
    sort: { value: sort, set: withPageReset(setSort) },
    selection,
    listQuery,
    ...extra,
  };
};
